import { Avatar, Tooltip as AntTooltip, Select } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import {
  CalendarClock,
  CalendarDays,
  Clock3,
  Globe2,
  MapPin,
  Video,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import type {
  SchedulePublicAvailableSlot,
  SchedulePublicBookingDetails,
  SchedulePublicBookingHost,
  SchedulePublicBookingLink,
  SchedulePublicHost,
} from "@/api/scheduling/schedule-public.types";
import { colors } from "@/design-system/theme/colors";
import { getTimezoneOptionsWith } from "@/utils/timezone";
import {
  formatSlotTime,
  formatLocationLabel,
  getBrandColor,
  getBrandTint,
  getDefaultLocation,
  getHostName,
  getLocationProviderLogo,
} from "../utils";

const DESCRIPTION_SCROLLBAR_CLASS = "public-booking-description-scroll";
const TIMEZONE_SELECT_CLASS = "public-booking-timezone-select";

interface EventDetailsPanelProps {
  bookingLink: SchedulePublicBookingLink;
  timezone: string;
  canSelectTimezone?: boolean;
  mode?: "booking" | "reschedule";
  existingBooking?: SchedulePublicBookingDetails | null;
  selectedDate?: Dayjs;
  selectedSlot?: SchedulePublicAvailableSlot | null;
  timeFormat?: "12h" | "24h";
  onTimezoneChange?: (timezone: string) => void;
}

const timezoneSelectStyles = `
  .${TIMEZONE_SELECT_CLASS} .ant-select-selector {
    padding-inline: 0 !important;
  }
  .${TIMEZONE_SELECT_CLASS} .ant-select-selection-item,
  .${TIMEZONE_SELECT_CLASS} .ant-select-selection-search-input {
    font-size: 15px;
    color: ${colors.textDefault};
  }
  .${TIMEZONE_SELECT_CLASS} .ant-select-selection-item {
    padding-inline-end: 18px !important;
    font-weight: 500;
  }
`;

const descriptionScrollbarStyles = `
  .${DESCRIPTION_SCROLLBAR_CLASS} {
    scrollbar-width: none;
    scrollbar-color: transparent transparent;
  }
  .${DESCRIPTION_SCROLLBAR_CLASS}:hover {
    scrollbar-width: thin;
    scrollbar-color: ${colors.surfaceDark} transparent;
  }
  .${DESCRIPTION_SCROLLBAR_CLASS}::-webkit-scrollbar {
    width: 0;
  }
  .${DESCRIPTION_SCROLLBAR_CLASS}:hover::-webkit-scrollbar {
    width: 4px;
  }
  .${DESCRIPTION_SCROLLBAR_CLASS}::-webkit-scrollbar-track {
    background: transparent;
  }
  .${DESCRIPTION_SCROLLBAR_CLASS}::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 999px;
  }
  .${DESCRIPTION_SCROLLBAR_CLASS}:hover::-webkit-scrollbar-thumb {
    background: ${colors.surfaceDark};
  }
`;

const LocationIcon = ({ type, label }: { type?: string; label?: string }) => {
  const logo = getLocationProviderLogo(type);
  if (logo) {
    return (
      <img
        src={logo}
        alt={label || "Meeting provider"}
        className="h-[25px] w-auto max-w-[96px] shrink-0 object-contain object-left"
        loading="lazy"
        decoding="async"
      />
    );
  }
  if (type === "custom")
    return (
      <MapPin size={19} strokeWidth={2} style={{ color: colors.textStrong }} />
    );
  return <Video size={19} strokeWidth={2} style={{ color: colors.textStrong }} />;
};

const getHostInitials = (name: string): string => {
  return name.trim().charAt(0).toUpperCase() || "N";
};

const getTrimmedText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getNumericId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }
  return null;
};

const getBookingHostUserId = (
  host?: SchedulePublicBookingHost | null,
): number | null =>
  getNumericId(host?.userId) ??
  getNumericId(host?.hostUserId) ??
  getNumericId(host?.user?.id);

const getBookingHostName = (
  host?: SchedulePublicBookingHost | null,
): string => {
  if (!host) return "";
  const nestedUser = host.user;
  const fullName = [
    host.userFirstName,
    host.userLastName,
    host.firstName,
    host.lastName,
    nestedUser?.firstName,
    nestedUser?.lastName,
  ]
    .map(getTrimmedText)
    .filter(Boolean)
    .join(" ");

  return (
    getTrimmedText(host.userName) ||
    getTrimmedText(host.name) ||
    getTrimmedText(nestedUser?.userName) ||
    getTrimmedText(nestedUser?.name) ||
    fullName ||
    getTrimmedText(host.userEmail) ||
    getTrimmedText(host.email) ||
    getTrimmedText(nestedUser?.email)
  );
};

const getBookingHostEmail = (
  host?: SchedulePublicBookingHost | null,
): string =>
  getTrimmedText(host?.userEmail) ||
  getTrimmedText(host?.email) ||
  getTrimmedText(host?.user?.email);

const findLinkedHost = (
  bookingLink: SchedulePublicBookingLink,
  assignedUserId: number | null,
  assignedBookingHost?: SchedulePublicBookingHost,
): SchedulePublicHost | undefined =>
  bookingLink.hosts?.find((host) => host.userId === assignedUserId) ??
  bookingLink.hosts?.find(
    (host) => getNumericId(assignedBookingHost?.id) === host.id,
  );

const getAssignedHostSummary = (
  bookingLink: SchedulePublicBookingLink,
  booking?: SchedulePublicBookingDetails | null,
): { name: string; email: string } | null => {
  const bookingHosts = booking?.hosts ?? [];
  if (!booking?.primaryHostUserId && bookingHosts.length === 0) return null;

  const assignedHostUserId =
    getNumericId(booking.primaryHostUserId) ??
    getBookingHostUserId(bookingHosts[0]);
  const assignedBookingHost =
    bookingHosts.find((host) => getBookingHostUserId(host) === assignedHostUserId) ??
    bookingHosts[0];
  const linkedHost = findLinkedHost(
    bookingLink,
    assignedHostUserId,
    assignedBookingHost,
  );
  const name = getBookingHostName(assignedBookingHost) || getHostName(linkedHost);
  const email =
    getBookingHostEmail(assignedBookingHost) || linkedHost?.userEmail?.trim() || "";

  if (!name && !email) return null;
  return { name, email };
};

const getBookingStartTime = (
  booking?: SchedulePublicBookingDetails | null,
): string => booking?.inviteeStartTime?.trim() || booking?.startTime?.trim() || "";

const getBookingEndTime = (
  booking?: SchedulePublicBookingDetails | null,
): string => booking?.inviteeEndTime?.trim() || booking?.endTime?.trim() || "";

const formatDateLabel = (value: string): string => {
  const date = dayjs(value);
  return date.isValid() ? date.format("dddd, MMMM D, YYYY") : value;
};

const BookingTimeLine = ({
  label,
  icon,
  children,
}: {
  label?: string;
  icon?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex items-start gap-3 leading-6">
    <span className="mt-0.5 flex h-[22px] min-w-[19px] shrink-0 items-center justify-start">
      {icon}
    </span>
    <span className="min-w-0">
      {label ? (
        <span
          className="block font-semibold"
          style={{ color: colors.textDarkest }}
        >
          {label}
        </span>
      ) : null}
      {children}
    </span>
  </div>
);

export const RescheduleTimeSummary = ({
  booking,
  selectedDate,
  selectedSlot,
  timeFormat,
}: {
  booking?: SchedulePublicBookingDetails | null;
  selectedDate?: Dayjs;
  selectedSlot?: SchedulePublicAvailableSlot | null;
  timeFormat: "12h" | "24h";
}) => {
  const formerStart = getBookingStartTime(booking);
  const formerEnd = getBookingEndTime(booking);
  const hasFormerTime = Boolean(formerStart);
  const hasNewTime = Boolean(selectedDate && selectedSlot);

  if (!hasFormerTime && !hasNewTime) return null;

  return (
    <>
      {hasFormerTime ? (
        <BookingTimeLine
          label="Former time"
          icon={
            <CalendarDays
              size={19}
              strokeWidth={2}
              style={{ color: colors.textStrong }}
            />
          }
        >
          <span className="block line-through decoration-1">
            {formatDateLabel(formerStart)}
          </span>
          <span className="block line-through decoration-1">
            {formatSlotTime(formerStart, timeFormat)}
            {formerEnd ? ` - ${formatSlotTime(formerEnd, timeFormat)}` : ""}
          </span>
        </BookingTimeLine>
      ) : null}

      {hasNewTime && selectedDate && selectedSlot ? (
        <BookingTimeLine
          icon={
            <CalendarDays
              size={19}
              strokeWidth={2}
              style={{ color: colors.textStrong }}
            />
          }
        >
          <span className="block font-semibold">
            {selectedDate.format("dddd, MMMM D, YYYY")}
          </span>
          <span className="block font-semibold">
            {formatSlotTime(selectedSlot.startTime, timeFormat)} -{" "}
            {formatSlotTime(selectedSlot.endTime, timeFormat)}
          </span>
        </BookingTimeLine>
      ) : null}
    </>
  );
};

interface PublicBookingHostsSummaryProps {
  bookingLink: SchedulePublicBookingLink;
  existingBooking?: SchedulePublicBookingDetails | null;
}

export const PublicBookingHostsSummary = ({
  bookingLink,
  existingBooking,
}: PublicBookingHostsSummaryProps) => {
  const hosts = useMemo(() => bookingLink.hosts || [], [bookingLink.hosts]);
  const assignedHost = useMemo(
    () => getAssignedHostSummary(bookingLink, existingBooking),
    [bookingLink, existingBooking],
  );
  const fallbackName = bookingLink.workspace.name || "Notifyy";
  const [selectedHostId, setSelectedHostId] = useState<number | null>(
    hosts[0]?.id ?? null,
  );
  const selectedHost =
    hosts.find((host) => host.id === selectedHostId) ?? hosts[0];
  const hostName =
    assignedHost?.name || getHostName(selectedHost) || fallbackName;
  const hostEmail = assignedHost?.email || "";

  // Host avatars are accented with the workspace brand color: the selected host
  // is a solid brand fill, the rest use a light tint of the same color.
  const brandColor = getBrandColor(bookingLink);
  const brandTint = getBrandTint(brandColor);

  return (
    <div className="mb-4 flex shrink-0 flex-col items-start gap-2">
      <div
        className="text-[10px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: colors.textMuted }}
      >
        Hosts
      </div>
      <Avatar.Group
        max={{
          count: 4,
          style: {
            color: brandColor,
            backgroundColor: brandTint,
            fontSize: 12,
            fontWeight: 700,
          },
        }}
        className="[&_.ant-avatar]:cursor-pointer"
      >
        {assignedHost ? (
          <Avatar
            size={28}
            style={{
              backgroundColor: brandColor,
              color: colors.white,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {getHostInitials(hostName)}
          </Avatar>
        ) : hosts.length > 0 ? (
          hosts.map((host) => {
            const name = getHostName(host) || fallbackName;
            const isSelected = host.id === selectedHost?.id;
            return (
              <AntTooltip key={host.id} title={name} placement="top">
                <Avatar
                  size={28}
                  onClick={() => setSelectedHostId(host.id)}
                  style={{
                    backgroundColor: isSelected ? brandColor : brandTint,
                    color: isSelected ? colors.white : brandColor,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {getHostInitials(name)}
                </Avatar>
              </AntTooltip>
            );
          })
        ) : (
          <Avatar
            size={28}
            style={{
              backgroundColor: brandTint,
              color: brandColor,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {getHostInitials(hostName)}
          </Avatar>
        )}
      </Avatar.Group>

      <div className="space-y-1.5">
        <div
          className="text-[12px] font-medium leading-4"
          style={{ color: colors.textDefault }}
        >
          {hostName}
        </div>
        {hostEmail ? (
          <div
            className="text-[12px] leading-4"
            style={{ color: colors.textMuted }}
          >
            {hostEmail}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const EventDetailsPanel = ({
  bookingLink,
  timezone,
  canSelectTimezone = false,
  mode = "booking",
  existingBooking,
  selectedDate,
  selectedSlot,
  timeFormat = "12h",
  onTimezoneChange,
}: EventDetailsPanelProps) => {
  const location = getDefaultLocation(bookingLink.locations);
  const locationLabel = formatLocationLabel(location);
  const showDescription =
    bookingLink.settings?.showEventDescription !== false &&
    Boolean(bookingLink.description?.trim());

  const allowTimezoneSelect = canSelectTimezone && Boolean(onTimezoneChange);
  const timezoneOptions = useMemo(
    () => (allowTimezoneSelect ? getTimezoneOptionsWith(timezone) : []),
    [allowTimezoneSelect, timezone],
  );

  return (
    <aside
      className="flex min-h-[260px] flex-col border-b p-5 sm:min-h-[300px] sm:p-6 lg:h-full lg:min-h-0 lg:border-b-0 lg:border-r"
      style={{ borderColor: colors.surfaceSubtle }}
    >
      <style>{descriptionScrollbarStyles}</style>
      <style>{timezoneSelectStyles}</style>
      <div className="shrink-0">
        <PublicBookingHostsSummary
          bookingLink={bookingLink}
          existingBooking={existingBooking}
        />
        <h1
          className="mb-4 text-[22px] font-semibold leading-[1.18] tracking-normal sm:text-[24px]"
          style={{ color: colors.textDarkest }}
        >
          {bookingLink.name}
        </h1>
      </div>

      {showDescription ? (
        <div
          className={`${DESCRIPTION_SCROLLBAR_CLASS} mt-1 min-h-0 flex-1 overflow-y-auto pr-3 text-[14px] leading-[1.55]`}
          style={{
            color: colors.textDefault,
          }}
        >
          {bookingLink.description}
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div
        className="mt-6 shrink-0 space-y-3.5 text-[15px]"
        style={{ color: colors.textDefault }}
      >
        {mode === "reschedule" ? (
          <RescheduleTimeSummary
            booking={existingBooking}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            timeFormat={timeFormat}
          />
        ) : null}
        <div className="flex items-center gap-3 leading-6">
          <Clock3 size={19} strokeWidth={2} style={{ color: colors.textStrong }} />
          <span>{bookingLink.durationMinutes}m</span>
        </div>
        <div className="flex items-center gap-3 leading-6">
          <span className="flex h-[25px] min-w-[19px] shrink-0 items-center justify-start">
            <LocationIcon type={location?.locationType} label={locationLabel} />
          </span>
          <span>{locationLabel}</span>
        </div>
        <div className="flex items-center gap-3 leading-6">
          <Globe2 size={19} strokeWidth={2} style={{ color: colors.textStrong }} />
          {allowTimezoneSelect ? (
            <Select
              value={timezone}
              onChange={(value) => onTimezoneChange?.(String(value))}
              options={timezoneOptions}
              showSearch
              optionFilterProp="label"
              variant="borderless"
              size="middle"
              popupMatchSelectWidth={300}
              aria-label="Select timezone"
              className={`${TIMEZONE_SELECT_CLASS} min-w-0 flex-1`}
              title={timezone}
            />
          ) : (
            <span>{timezone}</span>
          )}
        </div>
        {bookingLink.eventTypeMode === "round_robin" && (
          <div className="flex items-center gap-3 leading-6">
            <CalendarClock
              size={19}
              strokeWidth={2}
              style={{ color: colors.textStrong }}
            />
            <span>{bookingLink.hosts.length} available hosts</span>
          </div>
        )}
      </div>
    </aside>
  );
};
